import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';

export { useSlowWait } from '../../lib/useSlowWait';

/**
 * Данные админки живут ВНЕ компонента.
 *
 * `AdminPanel` монтируется заново при каждом заходе на /admin, и пока состояние
 * жило внутри него, каждый заход обязан был пройти путь «пусто → запрос →
 * данные». Отсюда и вспышка на кадр: экран честно рисовал то, что знал, а знал
 * он в этот момент ничего. Снапшот в модуле переживает размонтирование, поэтому
 * второй и все следующие заходы рисуются сразу целиком, а свежесть догоняет
 * фоновым кругом опроса (stale-while-revalidate).
 *
 * Запрос дедуплицируется: два компонента (панель и usage) на одном круге не
 * задваивают шесть эндпоинтов.
 */

let adminSnapshot = null;
let adminInflight = null;
let usageSnapshot = null;
let usageInflight = null;

/** Разлогинились — снапшот чужой сессии в этой вкладке не оставляем. */
export function clearAdminCache() {
  adminSnapshot = null;
  usageSnapshot = null;
}

async function loadAdmin() {
  const [a, r, ru, c, u, m] = await Promise.all([
    api.adminAgents(), api.adminRuns(), api.adminRules(), api.contexts(), api.adminUsers(), api.models(),
  ]);
  adminSnapshot = {
    agents: a.agents,
    profiles: a.profiles,
    runnerTypes: a.runnerTypes,
    runs: r.runs,
    rules: ru.rules,
    contexts: c.contexts,
    pendingUsers: (u.users || []).filter(x => x.status === 'pending'),
    models: m.models || [],
  };
  return adminSnapshot;
}

function fetchAdmin() {
  if (!adminInflight) adminInflight = loadAdmin().finally(() => { adminInflight = null; });
  return adminInflight;
}

function fetchUsage() {
  if (!usageInflight) {
    usageInflight = api.adminUsage()
      .then(u => { usageSnapshot = u; return u; })
      .finally(() => { usageInflight = null; });
  }
  return usageInflight;
}

/** Общий круг опроса: молчит, пока вкладка в фоне — там обновлять нечего и некому. */
function usePolling(refresh, intervalMs) {
  useEffect(() => {
    refresh();
    const tick = () => { if (document.visibilityState === 'visible') refresh(); };
    const t = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [refresh, intervalMs]);
}

/** Снапшот админки + фоновое обновление. `data` — null только до самого первого ответа. */
export function useAdminData(intervalMs = 5000) {
  const [data, setData] = useState(() => adminSnapshot);
  const [stale, setStale] = useState('');

  const refresh = useCallback(async () => {
    try {
      setData(await fetchAdmin());
      setStale('');
    } catch (e) { setStale(e.message); }
  }, []);

  usePolling(refresh, intervalMs);
  return { data, stale, refresh };
}

/** То же для расхода: свой снапшот и свой, более редкий круг. */
export function useUsageData(intervalMs = 30000) {
  const [usage, setUsage] = useState(() => usageSnapshot);

  const refresh = useCallback(async () => {
    try { setUsage(await fetchUsage()); } catch { /* показываем прошлый снапшот */ }
  }, []);

  usePolling(refresh, intervalMs);
  return usage;
}
